import React from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  InputLabel,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import type { GoogleSpreadsheet } from "./googleSheetsApi";

interface GoogleSheetsListProps {
  sheets: GoogleSpreadsheet[];
  isLoading?: boolean;
  errorMessage?: string;
  onRemoveSheet?: (fileId: string) => void | Promise<void>;
  removingFileIds?: string[];
}

const GoogleSheetsList: React.FC<GoogleSheetsListProps> = ({
  sheets,
  isLoading = false,
  errorMessage,
  onRemoveSheet,
  removingFileIds = [],
}) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          <Trans context="Message shown while connected spreadsheets are loading">
            Loading connected spreadsheets...
          </Trans>
        </Typography>
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="error">
          {errorMessage}
        </Typography>
      </Box>
    );
  }

  if (sheets.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <Trans context="Message shown when there are no connected spreadsheets">
            No connected spreadsheets yet.
          </Trans>
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        <Trans context="Heading for connected spreadsheets list in integrations">
          Connected spreadsheets
        </Trans>
      </Typography>

      {sheets.map((sheet, index) => {
        const isRemoving = removingFileIds.includes(sheet.fileId);

        return (
          <Box
            key={sheet.fileId}
            sx={{
              py: 1,
              borderTop: index === 0 ? "1px solid rgba(0, 0, 0, 0.08)" : "none",
              borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <InputLabel sx={{ whiteSpace: "normal", lineHeight: 1.4 }}>
              {index + 1}: {sheet.name} -{" "}
              <Trans context="Label prefix for spreadsheet owner email">
                Added by:
              </Trans>{" "}
              {sheet.added_by}
            </InputLabel>

            <Tooltip
              title={i18n._(
                msg({
                  context: "Button title to remove a connected spreadsheet",
                  message: "Remove",
                }),
              )}
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  aria-label="remove-connected-spreadsheet"
                  disabled={isRemoving || !onRemoveSheet}
                  onClick={() => onRemoveSheet?.(sheet.fileId)}
                >
                  <DeleteIcon
                    fontSize="small"
                    style={{ color: theme.palette.primary.main }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
};

export default GoogleSheetsList;
